<template>
  <Button
    class="flex-start"
    @click="onOpenDialog"
    severity="secondary"
    :disabled="props.disabled"
    v-tooltip:text="label ? label : `Ver registros de auditoría`"
    icon="pi pi-file"
  >
    <img
      src="../../assets/custom_icons/audit-icon.png"
      alt="Audit icon"
      width="22"
      height="22"
    />
  </Button>
  <Dialog v-model:visible="openDialog" class="p-dialog-maximized" modal>
    <template #header>
      <div
        class="flex flex-row justify-content-between align-items-center w-12"
      >
        <HeadTitle
          :title="label ? label : `Registros de auditoría`"
          description="Registros de auditoría de los cambios realizados en la entidad"
        />
        <div class="flex flex-row align-items-center mr-6">
          <i class="pi pi-filter text-primary" v-tooltip.bottom="'Filtros'"></i>
          <div class="flex flex-column ml-3">
            <label for="createdDate" class="text-sm">Rango de fechas</label>
            <Calendar
              v-model="dateFilter"
              selectionMode="range"
              :manualInput="false"
              inputId="createdDate"
              showButtonBar
            ></Calendar>
          </div>
          <div class="flex flex-column ml-3">
            <label for="createdDate" class="text-sm">Operación</label>
            <Dropdown
              v-model="operation"
              :options="operations"
              optionLabel="label"
              style="width: 230px"
            >
            </Dropdown>
          </div>
          <div class="flex flex-row align-items-center mt-3 ml-3">
            <Button
              icon="pi pi-search"
              severity="secondary"
              class="border-noround border-round-left"
              v-tooltip.bottom="'Buscar'"
              @click="onFilters"
            />
            <Button
              icon="pi pi-file-excel"
              severity="secondary"
              class="border-noround text-600-green"
              v-tooltip.bottom="'Generar reporte'"
              @click="onDownloadReport"
            />
            <Button
              icon="pi pi-undo"
              severity="secondary"
              class="border-noround border-round-right"
              v-tooltip.bottom="'Limpiar filtros'"
              @click="clearFilters"
            />
          </div>
        </div>
      </div>
    </template>
    <div class="flex flex-column h-full">
      <AuditTable :data="audits.data" :onClose="onClose" class="flex-grow-0" />
      <Paginator
        :meta="audits.meta"
        @pageChange="handlePageChange"
        @pageLimitChange="handlePageLimitChange"
        class="mt-3 mr-6 align-self-end"
      >
      </Paginator>
    </div>
  </Dialog>
</template>
<script setup lang="ts">
  import { reactive, ref } from 'vue'
  import PaginatedResult from '../dtos/PaginatedResult'
  import Audit from '../models/audit'
  import Paginator from './Paginator.vue'
  import {
    getAudits,
    downloadAuditReport,
    Operation,
  } from '../services/auditService'
  import Dialog from 'primevue/dialog'
  import AuditTable from './AuditTable.vue'
  import HeadTitle from './HeadTitle.vue'
  import useLoading from '@/store/load'
  const openDialog = ref(false)
  const dateFilter = ref()
  const operation = ref()

  const loadingStore = useLoading()

  const operations = [
    { label: 'Creación', val: 'CREATE' },
    { label: 'Actualización', val: 'UPDATE' },
    { label: 'Eliminación', val: 'DELETE' },
  ]

  const props = withDefaults(
    defineProps<{
      entities: string[]
      recordIds?: string[]
      rootId?: string
      onlyDeleted?: boolean
      label?: string
      disabled?: boolean
    }>(),
    {
      onlyDeleted: false,
      label: 'Ver registros de auditoria',
      includeRootId: false,
      disabled: false,
    }
  )

  const audits: PaginatedResult<Audit> = reactive({
    data: [],
    meta: {
      currentPage: 1,
      perPage: 10,
      lastPage: 1,
      total: 0,
      next: null,
      prev: null,
    },
  })

  const onOpenDialog = async () => {
    if (!openDialog.value) {
      openDialog.value = true
      await fetchAudits(audits.meta.currentPage, audits.meta.perPage)
    } else {
      openDialog.value = false
    }
  }

  // Función para cargar auditorías según la página y el tamaño de página
  const fetchAudits = async (page: number, perPage: number) => {
    loadingStore.setLoadingState()
    const response = await getAudits({
      entities: props.entities,
      recordIds: props.recordIds,
      rootId: props.rootId,
      onlyDeleted: props.onlyDeleted,
      pagination: {
        limit: perPage,
        page,
      },
      startDate: dateFilter.value ? dateFilter.value[0] : null,
      endDate: dateFilter.value ? dateFilter.value[1] : null,
      operation: operation.value?.val,
    })

    audits.data = response.data
    audits.meta = response.meta
    loadingStore.dismissLoadingState()
  }

  const handlePageChange = async (page: number) => {
    await fetchAudits(page, audits.meta.perPage)
  }

  const handlePageLimitChange = async (limit: number) => {
    audits.meta.perPage = limit
    audits.meta.currentPage = 1 // Reiniciar a la primera página
    await fetchAudits(audits.meta.currentPage, audits.meta.perPage)
  }

  const onDownloadReport = async () => {
    loadingStore.setLoadingState()
    await downloadAuditReport({
      entities: props.entities,
      recordIds: props.recordIds,
      rootId: props.rootId,
      onlyDeleted: props.onlyDeleted,
      startDate: dateFilter.value ? dateFilter.value[0] : null,
      endDate: dateFilter.value ? dateFilter.value[1] : null,
      operation: operation.value?.val,
      pagination: {
        limit: audits.meta.perPage,
        page: audits.meta.currentPage,
      },
    })
    loadingStore.dismissLoadingState()
  }

  const onClose = () => {}

  const clearFilters = () => {
    dateFilter.value = null
    operation.value = null
  }

  const onFilters = async () => {
    await fetchAudits(audits.meta.currentPage, audits.meta.perPage)
  }
</script>
